import React, { useCallback, useEffect, useRef } from 'react';
import { useShortcutsBlocker } from 'packages/hooks/shortcuts/useShortcutsBlocker';
import { PREVIEW_CANVAS_ID } from 'packages/common/editor/editorHelper';
import { rgbToHex } from '../../utils';

type Props = {
    onPick: (hex: string) => void;
    onCancel: () => void;
};

export function CanvasDropper({ onPick, onCancel }: Props) {
    const zoomRef = useRef<HTMLCanvasElement>(null);
    const editorCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const hasHandledMissingCanvasRef = useRef(false);
    const { toggleShortcutsBlocker } = useShortcutsBlocker(onCancel);

    const hideZoomLens = useCallback(() => {
        if (zoomRef.current) {
            zoomRef.current.style.display = 'none';
        }
    }, []);

    useEffect(() => {
        toggleShortcutsBlocker(true);

        return () => toggleShortcutsBlocker(false);
    }, [toggleShortcutsBlocker]);

    const handleMissingCanvas = useCallback(() => {
        if (!hasHandledMissingCanvasRef.current) {
            hasHandledMissingCanvasRef.current = true;
            console.warn('[EyeDropper] Editor canvas not found in DOM');
            onCancel();
        }
    }, [onCancel]);

    const getEditorCanvas = useCallback((): HTMLCanvasElement | null => {
        if (editorCanvasRef.current) {
            return editorCanvasRef.current;
        }

        if (hasHandledMissingCanvasRef.current) {
            return null;
        }

        const canvas = document.querySelector<HTMLCanvasElement>(`#${PREVIEW_CANVAS_ID} canvas`);

        if (!canvas) {
            handleMissingCanvas();

            return null;
        }

        editorCanvasRef.current = canvas;

        return canvas;
    }, [handleMissingCanvas]);

    const handleMouseMove = (e: React.MouseEvent) => {
        const canvas = getEditorCanvas();
        const zoom = zoomRef.current;

        if (!canvas || !zoom) {
            hideZoomLens();

            return;
        }

        const ctx = canvas.getContext('2d');
        const zoomCtx = zoom.getContext('2d');

        if (!ctx || !zoomCtx) {
            hideZoomLens();

            return;
        }

        const rect = canvas.getBoundingClientRect();

        if (e.clientX < rect.left || e.clientX >= rect.right || e.clientY < rect.top || e.clientY >= rect.bottom) {
            hideZoomLens();

            return;
        }

        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;

        zoomCtx.clearRect(0, 0, zoom.width, zoom.height);
        zoomCtx.drawImage(canvas, Math.max(0, x - 5), Math.max(0, y - 5), 11, 11, 0, 0, zoom.width, zoom.height);

        const cellSize = zoom.width / 11;
        zoomCtx.strokeStyle = '#777';

        for (let gx = 0; gx < zoom.width; gx += cellSize) {
            zoomCtx.beginPath();
            zoomCtx.moveTo(gx, 0);
            zoomCtx.lineTo(gx, zoom.height);
            zoomCtx.stroke();
        }

        for (let gy = 0; gy < zoom.height; gy += cellSize) {
            zoomCtx.beginPath();
            zoomCtx.moveTo(0, gy);
            zoomCtx.lineTo(zoom.width, gy);
            zoomCtx.stroke();
        }

        zoomCtx.strokeStyle = '#fff';
        zoomCtx.strokeRect(cellSize * 5, cellSize * 5, cellSize, cellSize);

        zoom.style.top = `${e.clientY + 16}px`;
        zoom.style.left = `${e.clientX + 16}px`;
        zoom.style.display = 'block';
    };

    const handleClick = (e: React.MouseEvent) => {
        const canvas = getEditorCanvas();

        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext('2d');

        if (!ctx) {
            console.warn('[EyeDropper] Canvas does not support 2D context (may be WebGL)');
            onCancel();

            return;
        }

        const rect = canvas.getBoundingClientRect();

        if (e.clientX < rect.left || e.clientX >= rect.right || e.clientY < rect.top || e.clientY >= rect.bottom) {
            return;
        }

        const x = Math.round((e.clientX - rect.left) * (canvas.width / rect.width));
        const y = Math.round((e.clientY - rect.top) * (canvas.height / rect.height));
        const clampedX = Math.min(Math.max(0, x), canvas.width - 1);
        const clampedY = Math.min(Math.max(0, y), canvas.height - 1);
        const px = ctx.getImageData(clampedX, clampedY, 1, 1).data;
        const hex = rgbToHex(`rgba(${px[0]},${px[1]},${px[2]},1)`);

        if (hex) {
            onPick(hex);
        } else {
            onCancel();
        }
    };

    return (
        <>
            <button
                type="button"
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 99999,
                    cursor: 'crosshair',
                    background: 'transparent',
                    border: 0,
                    padding: 0,
                }}
                onMouseMove={handleMouseMove}
                onClick={handleClick}
                data-testid="canvas-dropper-overlay"
                aria-label="Canvas color dropper overlay"
            />
            <canvas
                ref={zoomRef}
                width={110}
                height={110}
                data-testid="canvas-dropper-zoom"
                style={{
                    position: 'fixed',
                    display: 'none',
                    borderRadius: '50%',
                    border: '2px solid #fff',
                    zIndex: 100000,
                    pointerEvents: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                }}
            />
        </>
    );
}
